// src-lit/components/product/kd-variant-media.ts
import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { onEvent } from '../../utils/dom';
import { EVENT_NAMES, type VariantSelectedDetail } from '../../constants/events';

/**
 * @element kd-variant-media
 * @description バリアント選択に応じてフィーチャード画像を切り替える。
 *   kd-product-variant-selected イベントを購読し、
 *   featuredMedia.id に一致する画像を先頭に移動する。
 *
 * media-id-prefix 属性:
 *   - 未設定: section-id を prefix として使用（例: [data-media-id="section123-456"]）
 *   - 空文字: prefix なし（例: [data-media-id="456"]）
 *   - 値あり: その値を prefix として使用
 */
@customElement('kd-variant-media')
export class KdVariantMedia extends LitElement {
  createRenderRoot() {
    return this;
  }

  /** セクションID（後方互換性のため維持） */
  @property({ type: String, attribute: 'section-id' })
  sectionId = '';

  /** 画像 data-media-id の prefix（未設定の場合は section-id を使用、空文字の場合は prefix なし） */
  @property({ type: String, attribute: 'media-id-prefix' })
  mediaIdPrefix: string | undefined = undefined;

  private unsubscribe: (() => void) | null = null;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.unsubscribe = onEvent<VariantSelectedDetail>(
      document,
      EVENT_NAMES.PRODUCT.VARIANT_SELECTED,
      (e) => this.handleVariantSelected(e.detail),
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private handleVariantSelected(detail: VariantSelectedDetail): void {
    if (!detail.featuredMedia) return;

    const mediaId = detail.featuredMedia.id;
    const prefix = this.mediaIdPrefix !== undefined ? this.mediaIdPrefix : this.sectionId;
    const selector = prefix ? `[data-media-id="${prefix}-${mediaId}"]` : `[data-media-id="${mediaId}"]`;
    const newMedia = document.querySelector(selector);
    if (!newMedia) return;

    const parent = newMedia.parentElement;
    if (!parent || parent.firstElementChild === newMedia) return;
    parent.prepend(newMedia);

    window.setTimeout(() => {
      parent.scrollLeft = 0;
      parent.querySelector('li.product__media-item')?.scrollIntoView?.({ behavior: 'smooth' });
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-variant-media': KdVariantMedia;
  }
}
